// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperStyle } from "@guestsupply/App/Components/XngCarousel/XngCarousel";
import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { sectionTitleFields } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { customXngageWidget, fontFamilyValues, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    fontFamily = "fontFamily",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.fontFamily]: string;
        [fields.viewMoreText]: string;
        [fields.viewMoreLink]: string;
        [fields.border]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    categoriesDataView: getCategoriesDataView(state),
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
});

const mapDispatchToProps = {
    loadCategories,
    // xng-change: add google event
    xngGaGeneralEventEvent,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedCategoriesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    categoriesWrapper?: InjectableCss;
    categoryWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    image?: LazyImageProps;
    imageWrapper?: InjectableCss;
    swiperStyles?: SwiperStyle;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;

const FeaturedCategoriesWidget: React.FC<Props> = ({
    fields: { title, fontFamily, viewMoreText, viewMoreLink, border },
    loadCategories,
    categoriesDataView,
    isTabletScreen,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}) => {
    const [selectedCategories, setSelectedCategories] = React.useState<Category[]>([]);

    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        const tempCategories = categoriesDataView.value?.filter(category => category.isFeatured);
        if (tempCategories?.length) {
            setSelectedCategories(tempCategories);
        }
    }, [categoriesDataView]);

    if (categoriesDataView.isLoading || !categoriesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="shopByCategory_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    interface CategoryCardProps {
        category: Category;
    }

    const CategoryCard: FC<CategoryCardProps> = ({ category }) => {
        return (
            <StyledWrapper key={category.id} {...styles.categoryWrapper}>
                <Clickable
                    href={category.path}
                    {...styles.imageWrapper}
                    aria-label="category image clickable"
                    // xng-change: add google event
                    onClick={() =>
                        xngGaGeneralEventEvent({
                            eventType: GOOGLE_EVENTS.POPULAR_CATEGORIES,
                            includeEvent: xGoogleAnalytics.includePopularCategoriesEvent,
                            clickText: category.shortDescription,
                            destination: category.path,
                        })
                    }
                >
                    <LazyImage
                        width="250"
                        height="125"
                        src={category.smallImagePath}
                        altText={category.shortDescription}
                        customTransformations={{ default: "GUEST_4ColumnContainer_300x150" }}
                        {...styles.image}
                    />
                </Clickable>
                <Link
                    href={category.path}
                    {...styles.link}
                    area-label={category.shortDescription}
                    // xng-change: add google event
                    onClick={() =>
                        xngGaGeneralEventEvent({
                            eventType: GOOGLE_EVENTS.POPULAR_CATEGORIES,
                            includeEvent: xGoogleAnalytics.includePopularCategoriesEvent,
                            clickText: category.shortDescription,
                            destination: category.path,
                        })
                    }
                >
                    {category.shortDescription}
                </Link>
            </StyledWrapper>
        );
    };

    if (selectedCategories.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {createWidgetElement("SectionTitle/SectionTitle", {
                fields: { title, fontFamily, viewMoreText, viewMoreLink, border },
            })}
            {isTabletScreen ? (
                <XngCarousel
                    extendedStyles={styles.swiperStyles}
                    navigation
                    scrollbar
                    draggable={false}
                    breakpoints={{
                        480: {
                            slidesPerView: 1,
                        },
                        768: {
                            slidesPerView: 3,
                            spaceBetween: 20,
                        },
                    }}
                >
                    {selectedCategories?.map(category => (
                        <CategoryCard key={category.id} category={category} />
                    ))}
                </XngCarousel>
            ) : (
                <StyledWrapper {...styles.categoriesWrapper}>
                    {selectedCategories?.map(category => (
                        <CategoryCard key={category.id} category={category} />
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
// XNG-CHANGS : GUEST-2654 : Removing isRequired
const featuredCategoriesTitleFields = [...sectionTitleFields];
featuredCategoriesTitleFields.forEach((field, index) => {
    if (field.displayName?.toLowerCase() === "title") {
        featuredCategoriesTitleFields[index].isRequired = false;
    }
});

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategoriesWidget),
    definition: {
        group: customXngageWidget as any,
        fieldDefinitions: [...(featuredCategoriesTitleFields as any)],
    },
};

export default widgetModule;
